import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import root from "../assets/styles/Root.module.css";
import s from "../assets/styles/CasesPage.module.css";
import SpinInfoModal from "../components/SpinInfoModal";
import PrizeRevealAnimation from "../components/PrizeRevealAnimation";

function fmt(dt) {
   try {
      return new Date(dt).toLocaleString("ru-RU", {
         day: "2-digit", month: "2-digit", year: "numeric",
         hour: "2-digit", minute: "2-digit"
      });
   } catch { return ""; }
}


export default function CasesPage() {
   const API = process.env.REACT_APP_API_BASE_URL || "http://127.0.0.1:8002";
   const { isAuthenticated, authFetch, setProfile } = useAuth();
   const nav = useNavigate();

   const [items, setItems] = useState([]);
   const [loading, setLoading] = useState(true);
   const [err, setErr] = useState("");
   const [spinningId, setSpinningId] = useState(null);

   // PF: последние данные по кейсам
   const [lastPFByCaseId, setLastPFByCaseId] = useState({});
   const [pfModal, setPfModal] = useState({ open: false, data: null });
   
   // Анимация открытия кейса
   const [prizeAnimation, setPrizeAnimation] = useState({
      open: false,
      prizeAmount: 0,
      prizeTitle: null,
      hasBonus: false,
      bonusType: null,
      bonusMultiplier: null,
      bonusPrizeInfo: null, // Новое поле для бонуса-приза
      bonusDescription: null,
      baseAmount: null,
      bonusSpins: null,
      isExtraSpin: false
   });
   
   // Функция для открытия PF дополнительного спина
   const handleOpenExtraSpinPF = async (bonusSpinId, baseData) => {
      try {
         // Пытаемся получить как BonusSpin
         let r = await authFetch(`/api/cases/bonus-spins/${bonusSpinId}/`);
         if (!r.ok) {
            // Если не получилось, пробуем как обычный Spin (для обратной совместимости)
            r = await authFetch(`/api/cases/spins/${bonusSpinId}/`);
         }
         if (r.ok) {
            const spinData = await r.json();
            
            const pfData = {
               serverSeedHash: spinData.server_seed_hash,
               serverSeed: spinData.server_seed,
               clientSeed: spinData.client_seed || baseData?.clientSeed,
               nonce: spinData.nonce,
               rollDigest: spinData.roll_digest,
               rngValue: spinData.rng_value,
               prize: spinData.case_prize || spinData.prize,
               amount_usd: spinData.actual_amount_usd || spinData.amount_usd,
               spinId: spinData.id,
               is_extra_open: true,
               parent_spin_id: spinData.parent_spin_id
            };
            
            // Сразу открываем PF модалку без анимации
            setPfModal({ open: true, data: pfData });
         }
      } catch (e) {
         alert("Не удалось загрузить данные дополнительного открытия");
      }
   };

   useEffect(() => {
      let alive = true;
      (async () => {
         try {
            setErr(""); setLoading(true);
            const r = await fetch(`${API}/api/cases/?all=1`);
            const d = await r.json();
            if (alive) setItems((Array.isArray(d) ? d : []).filter(c => c.is_active));
         } catch {
            if (alive) setErr("Не удалось загрузить кейсы");
         } finally {
            if (alive) setLoading(false);
         }
      })();
      return () => { alive = false; };
   }, [API]);

   const handleSpin = async (id) => {
      if (!isAuthenticated) {
         nav("/login", { state: { from: { pathname: "/cases" } } });
         return;
      }
      setSpinningId(id);
      try {
         const r = await authFetch(`/api/cases/${id}/spin/`, { method: "POST" });
         const d = await r.json().catch(() => ({}));
         if (r.ok) {
            setItems(prev => prev.map(c => (c.id === id ? { ...c, ...d.case } : c)));
            if (d.profile) setProfile(d.profile);

            const spin = d?.spin || {};
            const prizeUsd = spin.actual_amount_usd ?? spin.amount_usd ?? spin.prize?.amount_usd ?? "?";
            const baseAmount = spin.base_amount_usd;
            const hasBonus = spin.has_bonus;
            
            // Новая бонусная система: проверяем bonus_prize_data (JSONField)
            let bonusPrizeData = null;
            if (spin.bonus_prize_data && typeof spin.bonus_prize_data === 'object') {
               bonusPrizeData = spin.bonus_prize_data;
            } else if (typeof spin.bonus_prize_data === 'string') {
               try {
                  bonusPrizeData = JSON.parse(spin.bonus_prize_data);
               } catch (e) {
                  // Игнорируем ошибку парсинга
               }
            }
            
            // Определяем тип и данные бонуса
            let bonusType = spin.bonus_type;
            let bonusMultiplier = spin.bonus_multiplier;
            let bonusPrizeInfo = null; // Для нового типа "prize"
            
            if (bonusPrizeData) {
               // Используем данные из новой структуры
               bonusType = bonusPrizeData.bonus_type;
               if (bonusType === "multiplier") {
                  bonusMultiplier = bonusPrizeData.multiplier;
               } else if (bonusType === "prize") {
                  // Бонус - обычный приз
                  bonusPrizeInfo = {
                     prize_id: bonusPrizeData.prize_id,
                     amount: bonusPrizeData.amount || bonusPrizeData.amount_usd,
                     prize_name: bonusPrizeData.prize_name || null
                  };
               }
            }
            
            const bonusDescription = spin.bonus_description;
            const prizeTitle = spin.prize?.title || spin.prize?.name || spin.case_prize?.prize?.name || null;
            
            // Загружаем информацию о дополнительных спинах для анимации
            let bonusSpinsForAnimation = null;
            if (hasBonus && bonusType === "extra_open") {
               const bonusSpinsSource = spin.bonus_spins_list || spin.bonus_spins || [];
               if (Array.isArray(bonusSpinsSource) && bonusSpinsSource.length > 0) {
                  bonusSpinsForAnimation = bonusSpinsSource.map(bs => ({
                     amount: bs.amount || bs.actual_amount_usd || 0,
                     bonus_spin_id: bs.bonus_spin_id || bs.spin_id || bs.spinId || bs.id
                  }));
               }
            }
            
            // Показываем анимацию открытия кейса
            setPrizeAnimation({
               open: true,
               prizeAmount: prizeUsd,
               prizeTitle: prizeTitle,
               hasBonus: hasBonus || false,
               bonusType: bonusType || null,
               bonusMultiplier: bonusMultiplier || null,
               bonusPrizeInfo: bonusPrizeInfo || null,
               bonusDescription: bonusDescription || null,
               baseAmount: baseAmount || null,
               bonusSpins: bonusSpinsForAnimation,
               isExtraSpin: false
            });

            const pf = d?.provably_fair || null;
            if (pf) {
               // Загружаем полную информацию о дополнительных спинах, если они есть
               const bonusSpinsData = [];
               if (hasBonus && bonusType === "extra_open") {
                  // Используем bonus_spins_list если доступен, иначе старое поле bonus_spins
                  const bonusSpinsSource = spin.bonus_spins_list || spin.bonus_spins || [];
                  if (Array.isArray(bonusSpinsSource) && bonusSpinsSource.length > 0) {
                     bonusSpinsData.push(...bonusSpinsSource.map(bs => ({
                        ...bs,
                        // Убеждаемся, что bonus_spin_id или spin_id присутствует
                        bonus_spin_id: bs.bonus_spin_id || bs.spin_id || bs.spinId || bs.id,
                        spin_id: bs.bonus_spin_id || bs.spin_id || bs.spinId || bs.id,
                        spinId: bs.bonus_spin_id || bs.spin_id || bs.spinId || bs.id,
                        serverSeed: pf.serverSeed,  // Используем тот же server_seed
                        serverSeedHash: pf.serverSeedHash,
                        clientSeed: pf.clientSeed,
                     })));
                  }
               }
               
               const payload = {
                  ...pf,
                  prize: spin.prize || spin.case_prize || null,
                  amount_usd: spin.amount_usd || spin.actual_amount_usd || null,
                  spinId: spin.id || null,
                  // Бонусная информация
                  has_bonus: hasBonus,
                  bonus_type: bonusType,
                  bonus_multiplier: bonusMultiplier,
                  bonus_prize_data: bonusPrizeData || null, // Новое поле для новой бонусной системы
                  base_amount_usd: baseAmount,
                  bonus_description: bonusDescription,
                  bonus_spins: bonusSpinsData.length > 0 ? bonusSpinsData : (spin.bonus_spins || []),
                  actual_amount_usd: spin.actual_amount_usd
               };
               setLastPFByCaseId(prev => ({ ...prev, [id]: payload }));
               setPfModal({ open: true, data: payload });
            }
         } else {
            alert(d?.detail || "Не удалось прокрутить");
         }
      } finally {
         setSpinningId(null);
      }
   };

   const openInfo = (caseId) => {
      const data = lastPFByCaseId[caseId];
      if (!data) return;
      setPfModal({ open: true, data });
   };

   return (
      <div className={root.container}>
         <h1 className={s.title}>Кейсы</h1>
         {err && <div className={s.error}>{err}</div>}
         {loading && <div className={s.loading}>Загрузка…</div>}
         <div className={s.grid}>
            {items.map((c) => {
               const isLimited = !!c?.type?.is_limited;
               const isTimed = !!c?.type?.is_timed;
               const availableFrom = c?.available_from ? new Date(c.available_from) : null;
               const availableTo = c?.available_to ? new Date(c.available_to) : null;
               const canSpin =
                  c.is_available_now &&
                  (!isLimited || (c.spins_remaining ?? Infinity) > 0);
               const disabled = spinningId === c.id || !canSpin;
               let disabledReason = "";
               if (!c.is_available_now) {
                  if (isTimed && availableFrom && Date.now() < +availableFrom) {
                     disabledReason = `Доступен с ${fmt(availableFrom)}`;
                  } else if (isTimed && availableTo && Date.now() > +availableTo) {
                     disabledReason = "Время истекло";
                  } else {
                     disabledReason = "Кейс недоступен";
                  }
               } else if (isLimited && (c.spins_remaining ?? 0) <= 0) {
                  disabledReason = "Лимит круток исчерпан";
               }
               const hasPF = !!lastPFByCaseId[c.id];
               return (
                  <article key={c.id} className={s.card}>
                     <header className={s.cardHead}>
                        <h2 className={s.name}>{c.name}</h2>
                        <div className={s.badges}>
                           {c?.type?.type && <span className={s.badgeType}>{c.type.type}</span>}
                           {isTimed && availableFrom && Date.now() < +availableFrom && (
                              <span className={`${s.badge} ${s.badgeOutline}`}>с {fmt(availableFrom)}</span>
                           )}
                           {isTimed && availableTo && Date.now() > +availableTo && (
                              <span className={`${s.badge} ${s.badgeGray}`}>истёк</span>)}
                           {!canSpin && !isTimed && (
                              <span className={`${s.badge} ${s.badgeGray}`}>недоступен</span>
                           )}
                        </div>
                     </header>
                     <div className={s.meta}>
                        {isLimited && (
                           <div className={s.spins}>
                              Осталось круток: <b>{c.spins_remaining}</b>
                           </div>
                        )}
                     </div>
                     {isTimed && (
                        <div className={s.window}>
                           Доступность:&nbsp;
                           {availableFrom ? `с ${fmt(availableFrom)}` : "—"}
                           {availableTo ? ` до ${fmt(availableTo)}` : ""}
                        </div>
                     )}
                     <div className={s.actions} style={{ gap: 8 }}>
                        <div className={s.price}>${c.price_usd}</div>
                        <button
                           className={`${root.btnPrimary} ${s.btn}`}
                           onClick={() => handleSpin(c.id)}
                           disabled={disabled}
                           title={disabled ? disabledReason : ""}
                        >
                           {spinningId === c.id ? "Открываем…" : disabled ? "не доступен" : "Открыть"}
                        </button>

                        {hasPF && (
                           <button
                              className={`${root.btn} ${s.btn}`}
                              onClick={() => openInfo(c.id)}
                              title="Показать детали Provably Fair"
                           >
                              Info
                           </button>
                        )}
                     </div>
                  </article>
               )
            })}
         </div>
         <SpinInfoModal
            open={pfModal.open}
            onClose={() => setPfModal({ open: false, data: null })}
            data={pfModal.data}
            onOpenExtraSpin={handleOpenExtraSpinPF}
         />
         <PrizeRevealAnimation
            open={prizeAnimation.open}
            onClose={() => setPrizeAnimation({ ...prizeAnimation, open: false })}
            prizeAmount={prizeAnimation.prizeAmount}
            prizeTitle={prizeAnimation.prizeTitle}
            hasBonus={prizeAnimation.hasBonus}
            bonusType={prizeAnimation.bonusType}
            bonusMultiplier={prizeAnimation.bonusMultiplier}
            bonusPrizeInfo={prizeAnimation.bonusPrizeInfo}
            bonusDescription={prizeAnimation.bonusDescription}
            baseAmount={prizeAnimation.baseAmount}
            bonusSpins={prizeAnimation.bonusSpins}
            isExtraSpin={prizeAnimation.isExtraSpin}
         />
      </div>
   );
}
